import React from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import {
  Checkbox,
  Fieldset,
  Infobox,
  Label,
  Select,
  SelectOption,
  Text,
} from "@flixbus/honeycomb-react";
import type { DimensionTypeOption } from "../../../../hooks/useCreateCalculationPanel";
import type { CreateCalculationFormData } from "../../CreateCalculationPanel.constants";

const NONE_VALUE = "__none__";
const UNSELECTED_VALUE = "";

interface Step3DimensionProps {
  form: UseFormReturn<CreateCalculationFormData>;
  dimensionTypeOptions: DimensionTypeOption[];
  hasPicked: boolean;
  onPick: () => void;
}

// Edits one dimension selection that applies to every selected subcategory.
const Step3Dimension: React.FC<Step3DimensionProps> = ({
  form,
  dimensionTypeOptions,
  hasPicked,
  onPick,
}) => {
  const { control, setValue, watch } = form;
  const selectedTypeId = watch("dimensionTypeId");
  const selectedValues = watch("dimensionValues") ?? [];

  const selectedType = dimensionTypeOptions.find(
    (opt) => opt.id === selectedTypeId,
  );
  const seededValues = selectedType?.values ?? [];
  const showValueList = hasPicked && selectedTypeId !== null;

  const toggleValue = (value: string, checked: boolean) => {
    const next = checked
      ? [...selectedValues, value]
      : selectedValues.filter((v) => v !== value);
    setValue("dimensionValues", next, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <>
      <Controller
        name="dimensionTypeId"
        control={control}
        render={({ field }) => (
          <Select
            id="create-dimension-type"
            label={<Label text="Dimension*" />}
            placeholder="— Select —"
            value={hasPicked ? (field.value ?? NONE_VALUE) : UNSELECTED_VALUE}
            onChange={(e) => {
              const val = e.target.value;
              if (val === UNSELECTED_VALUE) return;
              onPick();
              const nextTypeId = val === NONE_VALUE ? null : val;
              field.onChange(nextTypeId);
              setValue("dimensionValues", [], {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
            onBlur={field.onBlur}
            innerRef={field.ref}
          >
            <SelectOption value="">{"— Select —"}</SelectOption>
            {dimensionTypeOptions.map((opt) => (
              <SelectOption key={opt.id} value={opt.id}>
                {opt.name}
              </SelectOption>
            ))}
            <SelectOption value={NONE_VALUE}>None</SelectOption>
          </Select>
        )}
      />
      {showValueList && seededValues.length > 0 && (
        <Fieldset
          legend={`Select ${selectedType?.name ?? "values"}`}
          itemSpacing="2"
        >
          {seededValues.map((value) => {
            const id = `create-dim-value-${value}`;
            return (
              <Checkbox
                key={value}
                id={id}
                label={value}
                value={value}
                checked={selectedValues.includes(value)}
                onChange={(e) => toggleValue(value, e.target.checked)}
              />
            );
          })}
        </Fieldset>
      )}
      {showValueList && seededValues.length === 0 && (
        <Infobox appearance="info" small>
          {`This ${selectedType?.name ?? "dimension"} has no seeded values yet. You can continue and pick values later, or ask an admin to seed values first.`}
        </Infobox>
      )}
      <Text small>
        Tip: The selected dimension and its chosen values apply to all
        subcategories chosen in the previous step.
      </Text>
    </>
  );
};

export default React.memo(Step3Dimension);
